export type PerfMetric = {
  type: string;
  name?: string;
  component?: string;
  duration?: number;
  value?: number;
  timestamp: number;
  extra?: any;
};

import { analyzeBatch } from "./regression";
import { queueStorage } from "./utils/storageUtils";

const QUEUE_KEY = "perf-metrics-queue";
const BATCH_SIZE = 25;
const FLUSH_INTERVAL = 30_000; // ms
const PERF_ENDPOINT = import.meta.env.VITE_PERF_ENDPOINT || "";

let queue: PerfMetric[] = [];
try {
  const raw = localStorage.getItem(QUEUE_KEY);
  if (raw) queue = JSON.parse(raw);
} catch {
  queue = [];
}

let flushTimer: number | null = null;

function scheduleFlush() {
  if (flushTimer) return;
  try {
    console.debug("[perf] scheduleFlush called, setting timer");
  } catch {}
  flushTimer = window.setTimeout(() => {
    try {
      console.debug("[perf] flush timer fired");
    } catch {}
    void flush();
  }, FLUSH_INTERVAL);
}

export async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  try {
    console.debug("[perf] flush invoked, queue length:", queue.length);
  } catch {}

  if (queue.length === 0) return;

  const batch = queue.splice(0, BATCH_SIZE);

  // Save a session snapshot of the batch so the dashboard can restore recent
  // metrics even if localStorage is cleared after a successful send.
  try {
    queueStorage.saveSnapshot(batch);
  } catch (e) {
    try {
      console.debug("[perf] queueStorage.saveSnapshot failed", e);
    } catch {}
  }

  // Run lightweight local regression analysis before sending.
  // Analysis is best-effort and must not block telemetry delivery.
  try {
    const alerts = analyzeBatch(batch);
    if (Array.isArray(alerts) && alerts.length) {
      for (const a of alerts) {
        // Surface regressions in client logs; integration with Sentry/other APM
        // can be done by customizing regression.onAlert.
        // Keep this warning minimal to avoid console noise in production.
        try {
          console.warn("[perf] regression detected", a);
        } catch {}
      }
    }
  } catch (e) {
    try {
      console.debug("[perf] analyzeBatch error", e);
    } catch {}
    // ignore analysis errors
  }

  try {
    if (PERF_ENDPOINT) {
      // send telemetry to configured endpoint (use keepalive for unload)
      await fetch(PERF_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch),
        keepalive: true,
      });
    } else {
      // fallback to console in absence of an endpoint
      console.debug("[perf] batch", batch);
    }
  } catch (err) {
    // restore batch on failure
    console.warn("[perf] send failed, re-queueing", err);
    queue.unshift(...batch);
  }

  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    try {
      console.debug("[perf] localStorage.setItem failed", e);
    } catch {}
    // ignore storage errors
  }
}

export function logMetric(metric: PerfMetric) {
  try {
    console.debug("[perf] logMetric", metric);
  } catch {}
  queue.push(metric);
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    try {
      // keep a lightweight session snapshot up-to-date for the dashboard UI
      queueStorage.saveSnapshot(queue);
    } catch (e) {
      try {
        console.debug("[perf] queueStorage.saveSnapshot (logMetric) failed", e);
      } catch {}
    }
  } catch (e) {
    try {
      console.debug("[perf] localStorage set failed in logMetric", e);
    } catch {}
    // ignore
  }

  if (queue.length >= BATCH_SIZE) {
    try {
      console.debug("[perf] batch size reached, flushing immediately");
    } catch {}
    void flush();
    return;
  }
  scheduleFlush();
}

export function startAutoFlush() {
  scheduleFlush();
}

export function clearQueue() {
  queue = [];
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch {
    // ignore
  }
}
